/**
 * Migrate World: what the owner has funded, what has come back, and what the pipeline
 * could bring. Pure functions; the screen only renders what these return.
 *
 * Funded, per month:
 *   before settings.cockpit_from  -> imported MW Ledger history (mw_costs, source 'ledger_history')
 *   from settings.cockpit_from on -> Migrate World debits in Cockpit (statements), plus this month's
 *                                    unmatched bank alerts filed as Migrate World
 *   any month                     -> costs paid outside tracked accounts (source 'off_account')
 * Received is only what the owner logged as paid out. The pipeline is never added to Received.
 */

export type MwCategory = 'Payroll' | 'Allowances' | 'Office' | 'Professional' | 'Marketing' | 'Statutory' | 'Other';
export const MW_CATEGORIES: MwCategory[] = ['Payroll', 'Allowances', 'Office', 'Professional', 'Marketing', 'Statutory', 'Other'];
export type MwStage = 'lead' | 'committed' | 'closed' | 'paid' | 'lost';
export type Ccy = 'NGN' | 'USD' | 'EUR';

export interface MwSettings { cockpit_from: string; usd_rate: number | null; eur_rate: number | null }
export interface MwCost {
  id: string; cost_date: string; category: MwCategory; description: string; payee: string | null;
  currency: Ccy; amount: number; rate_to_ngn: number; ngn: number; source: 'ledger_history' | 'off_account'; note: string | null;
}
export interface MwDeal {
  id: string; name: string; program: string | null; option: string | null; stage: MwStage; currency: Ccy;
  expected_share: number | null; share_max: number | null; notes: string | null;
}
export interface MwPayout { id: string; deal_id: string | null; received_on: string; currency: Ccy; amount: number; ngn: number | null; account: string | null; note: string | null }
/** A Migrate World payment seen in Cockpit (statement transaction or bank alert). */
export interface MwCockpitRow { tx_date: string; amount: number; currency: string; description: string; from: 'statement' | 'alert' }

export interface MwData { settings: MwSettings | null; costs: MwCost[]; deals: MwDeal[]; payouts: MwPayout[] }

/** Category for a Cockpit payment, from its narration. Unknown stays Other. */
export function mwCategoryOf(description: string): MwCategory {
  const d = description.toLowerCase();
  if (/salary|payroll|wage/.test(d)) return 'Payroll';
  if (/transport|airtime|\bdata\b|allowance|uber|bolt/.test(d)) return 'Allowances';
  if (/regus|rent|office|marabunta/.test(d)) return 'Office';
  if (/recruit|placement|conference|ticket|techcabal|linkedin|legal|lawyer|audit|consult/.test(d)) return 'Professional';
  if (/exhibition|print|brochure|marketing|advert|event|seed/.test(d)) return 'Marketing';
  if (/\bcac\b|tax|levy|filing|annual return|stamp/.test(d)) return 'Statutory';
  return 'Other';
}

export const monthOf = (date: string) => date.slice(0, 7);

/** NGN value of a foreign amount at the owner's planning rate. Null when there is no rate. */
export function atPlanningRate(amount: number | null, currency: Ccy, s: MwSettings | null): number | null {
  if (amount === null) return null;
  if (currency === 'NGN') return amount;
  const rate = currency === 'USD' ? s?.usd_rate : s?.eur_rate;
  return rate ? amount * rate : null;
}

export interface MwMonth { month: string; ngn: number; ledger: number; cockpit: number; offAccount: number }
export interface MwPipelineDeal extends MwDeal { ngnLow: number | null; ngnHigh: number | null }
export interface MwSummary {
  since: string | null;
  funded: number;
  months: MwMonth[];
  byCategory: { category: MwCategory; ngn: number }[];
  /** Payouts in NGN; foreign payouts without a recorded NGN value use the planning rate. */
  received: number;
  receivedAtPlanningRate: boolean;
  net: number;
  /** Average of the last three complete months that had MW costs. A month with no staff is not the run rate. */
  burn: number | null;
  burnMonths: string[];
  pipeline: MwPipelineDeal[];
  committedLow: number | null;
  committedHigh: number | null;
  /** Committed deals' expected shares (low end) still needed to recover what is not yet received. */
  breakEvenDeals: number | null;
  /** Months of current burn one typical committed share covers. */
  monthsPerDeal: number | null;
}

export function mwSummary(data: MwData, cockpit: MwCockpitRow[], today = new Date()): MwSummary {
  const s = data.settings;
  const cut = s?.cockpit_from ?? null;
  const months = new Map<string, MwMonth>();
  const byCat = new Map<MwCategory, number>();
  const add = (month: string, k: 'ledger' | 'cockpit' | 'offAccount', ngn: number, cat: MwCategory) => {
    const m = months.get(month) ?? { month, ngn: 0, ledger: 0, cockpit: 0, offAccount: 0 };
    m[k] += ngn; m.ngn += ngn;
    months.set(month, m);
    byCat.set(cat, (byCat.get(cat) ?? 0) + ngn);
  };
  for (const c of data.costs) {
    if (c.source === 'ledger_history' && cut && c.cost_date >= cut) continue; // Cockpit owns these months
    add(monthOf(c.cost_date), c.source === 'ledger_history' ? 'ledger' : 'offAccount', c.ngn, c.category);
  }
  if (cut) {
    for (const r of cockpit) {
      if (r.currency !== 'NGN' || r.tx_date < cut) continue;
      add(monthOf(r.tx_date), 'cockpit', r.amount, mwCategoryOf(r.description));
    }
  }
  // Every month from the first funded month to now, so a zero month shows as zero, not as a gap.
  const keys = [...months.keys()].sort();
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  if (keys.length) {
    const [y0, m0] = keys[0]!.split('-').map(Number) as [number, number];
    for (let y = y0, m = m0; `${y}-${String(m).padStart(2, '0')}` <= thisMonth; m === 12 ? (y++, m = 1) : m++) {
      const k = `${y}-${String(m).padStart(2, '0')}`;
      if (!months.has(k)) months.set(k, { month: k, ngn: 0, ledger: 0, cockpit: 0, offAccount: 0 });
    }
  }
  const list = [...months.values()].sort((a, b) => a.month.localeCompare(b.month));
  const funded = list.reduce((a, m) => a + m.ngn, 0);

  let receivedAtPlanningRate = false;
  const received = data.payouts.reduce((a, p) => {
    if (p.ngn !== null) return a + p.ngn;
    const v = atPlanningRate(p.amount, p.currency, s);
    if (v === null) return a;
    receivedAtPlanningRate = true;
    return a + v;
  }, 0);

  const complete = list.filter((m) => m.month < thisMonth && m.ngn > 0).slice(-3);
  const burn = complete.length ? complete.reduce((a, m) => a + m.ngn, 0) / complete.length : null;

  const pipeline = data.deals.filter((d) => d.stage !== 'paid' && d.stage !== 'lost').map((d) => ({
    ...d,
    ngnLow: atPlanningRate(d.expected_share, d.currency, s),
    ngnHigh: atPlanningRate(d.share_max ?? d.expected_share, d.currency, s),
  }));
  const committed = pipeline.filter((d) => d.stage === 'committed' || d.stage === 'closed');
  const sumOf = (k: 'ngnLow' | 'ngnHigh') => (committed.length && committed.every((d) => d[k] !== null)
    ? committed.reduce((a, d) => a + (d[k] ?? 0), 0) : null);
  const committedLow = sumOf('ngnLow');
  const committedHigh = sumOf('ngnHigh');
  const typical = committed.length && committedLow !== null ? committedLow / committed.length : null;
  const toRecover = Math.max(0, funded - received);

  return {
    since: list[0]?.month ?? null,
    funded,
    months: list,
    byCategory: MW_CATEGORIES.map((category) => ({ category, ngn: byCat.get(category) ?? 0 })).filter((c) => c.ngn > 0)
      .sort((a, b) => b.ngn - a.ngn),
    received,
    receivedAtPlanningRate,
    net: received - funded,
    burn,
    burnMonths: complete.map((m) => m.month),
    pipeline,
    committedLow,
    committedHigh,
    breakEvenDeals: typical ? Math.ceil(toRecover / typical) : null,
    monthsPerDeal: typical && burn ? typical / burn : null,
  };
}

/** Migrate World costs paid outside the tracked accounts in one month ("YYYY-MM"), in naira. */
export function mwOutsideFor(costs: MwCost[], monthKey: string): number {
  return costs.filter((c) => c.source === 'off_account' && c.cost_date.slice(0, 7) === monthKey).reduce((a, c) => a + c.ngn, 0);
}
